/**
 * Adding a row, from the blank row at the bottom or from outside the grid.
 *
 * What was typed shows as a stand-in row straight away while the real one is
 * created and loaded, so the value never vanishes. The cursor moves onto it
 * at once, and the real row takes its place.
 */

import { useRef, useState } from 'react';
import type { Row } from '@tanstack/react-table';
import type { CellMove } from './dom';
import type { CursorControls } from './useCursor';
import { NEW_ROW_ID } from './useCellMarks';
import type { NewRowConfig } from './types';

/** The value typed into the blank row, shown until its real row is drawn. */
export type StandIn = { columnId: string; value: string };

type PendingRow<T> = StandIn & {
  /** The created row's id, once `create` has returned it. */
  id: string | null;
  /** The data first drawn after the id came back; a later change ends the wait. */
  dataAtSettle?: T[];
};

/**
 * Where the rows below the data sit. A stand-in goes straight after the last
 * data row, and the blank row after that.
 */
export function belowDataRows(
  dataRowCount: number,
  standInShown: boolean,
  hasBlankRow: boolean
) {
  const standIns = standInShown ? 1 : 0;
  return {
    /** Where a new stand-in goes. */
    nextStandInIndex: dataRowCount,
    /** The stand-in drawn now, or -1. */
    standInIndex: standInShown ? dataRowCount : -1,
    newRowIndex: hasBlankRow ? dataRowCount + standIns : -1,
    /** Every row the cursor can reach. */
    rowCount: dataRowCount + standIns + (hasBlankRow ? 1 : 0),
  };
}

export function useStandIn<T>(rows: Row<T>[], data: T[]) {
  const [pending, setPending] = useState<PendingRow<T> | null>(null);
  // Whether a create is running, kept outside render: two adds in one event,
  // or a paste whose clipboard read held an old render's callbacks, must see
  // the first one at once rather than after the next render.
  const inFlightRef = useRef(false);
  const id = pending?.id ?? null;
  const arrived = id !== null && rows.some(row => row.id === id);
  // Adjusted while rendering rather than in effects, so no frame draws a
  // stand-in that is already done.
  if (pending && arrived) {
    // The real row is drawn: the stand-in is done, and a row that later
    // leaves the data does not bring it back.
    setPending(null);
  } else if (pending && id !== null && pending.dataAtSettle === undefined) {
    setPending({ ...pending, dataAtSettle: data });
  } else if (pending?.dataAtSettle && pending.dataAtSettle !== data) {
    // The data changed and the row is still not in it (filtered out, or the
    // reload failed): stop waiting for it.
    setPending(null);
  }
  const shown: StandIn | null = pending && !arrived ? pending : null;
  return {
    /** The stand-in to draw, or null. */
    shown,
    /** A create is running; once it has returned an id, the grid takes more. */
    creating: shown !== null && id === null,
    /** The created row the stand-in is waiting for, or null. */
    awaitedRowId: shown ? id : null,
    /** Claims the one create allowed at a time; false while one is running. */
    begin: () => {
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      return true;
    },
    /** The running create has settled. */
    end: () => {
      inFlightRef.current = false;
    },
    show: (columnId: string, value: string) =>
      setPending({ columnId, value, id: null }),
    settle: (createdId: string) =>
      setPending(prev => (prev ? { ...prev, id: createdId } : prev)),
    drop: () => setPending(null),
  };
}

export type StandInControls = ReturnType<typeof useStandIn>;

type CreateOptions = {
  /** Open the new row's cell for typing once it arrives. */
  edit: boolean;
  /** Where the cursor lands; null keeps its column (a Tab has moved it on). */
  focusColumnId: string | null;
  /** Leave the cursor this many rows from the new row, and stay there. */
  rowOffset: number;
};

type NewRowActionsOptions = {
  newRow: NewRowConfig | undefined;
  /** Where the stand-in for a new row goes (see belowDataRows). */
  nextStandInIndex: number;
  columnIds: readonly string[];
  standIn: StandInControls;
  append: (id: string) => void;
  cursor: Pick<CursorControls, 'place' | 'focusCreatedRow'>;
  refuse: (rowId: string, columnId: string) => void;
};

/** The two ways to add a row; see SpreadsheetHandle.addRow for the second. */
export function newRowActions({
  newRow,
  nextStandInIndex,
  columnIds,
  standIn,
  append,
  cursor,
  refuse,
}: NewRowActionsOptions) {
  /** The created row's id, or null when nothing was created. */
  const runCreate = async (
    create: () => Promise<string | null>,
    columnId: string,
    value: string,
    { edit, focusColumnId, rowOffset }: CreateOptions
  ): Promise<string | null> => {
    if (!standIn.begin()) return null;
    standIn.show(columnId, value);
    // Onto the stand-in now, not when the real row arrives: the column given,
    // or wherever the cursor already is.
    const col =
      focusColumnId === null ? null : columnIds.indexOf(focusColumnId);
    cursor.place(c => ({
      row: nextStandInIndex + rowOffset,
      col: col ?? c.col,
    }));
    // A failed create is one that created nothing.
    const id = await create().catch(() => null);
    standIn.end();
    if (id) {
      append(id);
      standIn.settle(id);
      if (rowOffset === 0) cursor.focusCreatedRow(id, focusColumnId, edit);
    } else {
      standIn.drop();
      refuse(NEW_ROW_ID, columnId);
    }
    return id;
  };

  /**
   * A value typed or pasted into the blank row. Enter moves on from the row
   * being added, as it does from any other: down to the fresh blank row, or
   * up with Shift.
   */
  const createRow = (columnId: string, value: string, move?: CellMove) => {
    const trimmed = value.trim();
    if (!newRow || !trimmed) return;
    void runCreate(
      () => newRow.onCreate(columnId, trimmed),
      columnId,
      trimmed,
      { edit: false, focusColumnId: null, rowOffset: move?.rows ?? 0 }
    );
  };

  const addRow = (create: () => Promise<string | null>, columnId: string) =>
    runCreate(create, columnId, '', {
      edit: true,
      focusColumnId: columnId,
      rowOffset: 0,
    });

  return { createRow, addRow };
}
